import React from 'react'
import { createPortal } from 'react-dom'
import { Observable } from 'rx'
import type { BettingType, Campaign } from '@mobi/betslip/types'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { observeImmutable } from '@core/Components/HOCs'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { state$ as investmentState$, InvestmentState } from '../BetInvestment/driver'
import { state$ as quickbetState$ } from '@core/Areas/Quickbet/driver'
import { state$ as superPickState$ } from '../SuperPick/Components/SuperPick/driver'
import { BonusBetListContainerStyled, BonusBetListItemContainerStyled } from './BonusBetList.styles'
import { Swiper } from '@core/Components/Swiper'
import { useFeature } from '@core/Utils/hooks'
import { groupBonusBets, isCampaignV2 } from '@core/Areas/Bonuses/data/helpers'
import type { CampaignV2 } from '@core/Areas/Bonuses/data/types'
import { BonusBetSplittingTray, useSplitBonusBetMutation } from '@core/Areas/Bonuses'
import { trackBonusOfferClick } from '@core/Areas/Bonuses/analytics'
import { BonusBetListItem } from './BonusBetListItem'

const state$ = Observable.combineLatest(
  userAccountState$,
  quickbetState$,
  investmentState$,
  superPickState$,
  (
    userAccountState,
    quickbetState,
    investmentRecord,
    superPickState
  ): BonusBetListStateProps & { canBet: boolean } => {
    const investmentState = investmentRecord.toJS() as InvestmentState

    return {
      isBusy: quickbetState.isBusy,
      canBet: quickbetState.canBet,
      campaigns: userAccountState.activeCampaigns
        ?.filter(x => x.rewardType == 'BonusBetReward')
        .toArray(),
      selectedBonusBetCampaignId: investmentState.bonusBet?.campaignId,
      hasSelectedSuperPick: !!superPickState.selectedSuperPickSeq,
      isEachWay: quickbetState.isEachWay,
    }
  }
)

type BonusBetListProps = BonusBetListStateProps & BonusBetListExternalProps
export type BonusBetListStateProps = {
  isBusy: boolean
  campaigns: Campaign[] | undefined
  selectedBonusBetCampaignId: number | undefined
  hasSelectedSuperPick: boolean
  isEachWay: boolean
}

export type BonusBetListExternalProps = {
  superPickEnabled?: boolean
  setBonusBet: (bonusBet: { campaignId: number; value: number } | null) => void
  bettingType?: BettingType | null
}

const CHUNK_SIZE_LARGE = 6
const CHUNK_SIZE_SMALL = 3

export const BonusBetListComponent = ({
  isBusy,
  campaigns,
  superPickEnabled,
  selectedBonusBetCampaignId,
  hasSelectedSuperPick,
  isEachWay,
  setBonusBet,
  bettingType,
}: BonusBetListProps): JSX.Element | null => {
  const [selectedBonusBets, setSelectedBonusBets] = React.useState<number[]>(
    selectedBonusBetCampaignId ? [selectedBonusBetCampaignId] : []
  )
  const [bonusBetToSplit, setBonusBetToSplit] = React.useState<CampaignV2 | null>(null)

  const isSplitBonusFeatureActive = useFeature(FeatureFlags.SPLIT_BONUS_BETS.key)
  const isCampaignsV2FeatureActive = useFeature(FeatureFlags.GET_CAMPAIGNS_V2.key)

  const isBetSplittingAndCampaignsV2Active = isSplitBonusFeatureActive && isCampaignsV2FeatureActive

  const groupedBonusBets =
    isBetSplittingAndCampaignsV2Active && campaigns ? groupBonusBets(campaigns) : {}

  const campaignsData = isBetSplittingAndCampaignsV2Active
    ? Object.keys(groupedBonusBets).map(key => groupedBonusBets[key][0])
    : campaigns

  const isSplitBonusBetTrayOpen = !!bonusBetToSplit

  const { mutate, isPending } = useSplitBonusBetMutation({
    updateBonusBetToSplit: () => setBonusBetToSplit(null),
  })

  const paginatedBonusBets = paginate(
    campaignsData,
    superPickEnabled ? CHUNK_SIZE_LARGE : CHUNK_SIZE_SMALL
  )

  React.useEffect(() => {
    if (!selectedBonusBetCampaignId) {
      setSelectedBonusBets([])
    } else {
      setSelectedBonusBets([selectedBonusBetCampaignId])
    }
  }, [selectedBonusBetCampaignId])

  React.useEffect(() => {
    if (!campaignsData) return

    const campaignId = selectedBonusBets.length ? selectedBonusBets[0] : null
    const selectedCampaign = campaignId ? campaignsData.find(x => x.id == campaignId) : null

    const bonusBet = selectedCampaign
      ? { campaignId: selectedCampaign.id, value: selectedCampaign.remainingAmount }
      : null

    if (bonusBet?.campaignId != selectedBonusBetCampaignId) {
      setBonusBet(bonusBet)
    }
  }, [campaignsData, selectedBonusBets, selectedBonusBetCampaignId, setBonusBet])

  const updateButtonState = React.useCallback(
    (id: number): void => {
      const isBonusSelected = selectedBonusBets.find(x => x == id)
      const newSelectedBonusBets = []

      if (!isBonusSelected) {
        newSelectedBonusBets.push(id)
      }

      setSelectedBonusBets(newSelectedBonusBets)
    },
    [selectedBonusBets]
  )

  const getButtonState = React.useCallback(
    (id: number): boolean | undefined => {
      return !!selectedBonusBets.find(x => x === id)
    },
    [selectedBonusBets]
  )

  if (!paginatedBonusBets || !paginatedBonusBets.length) {
    return null
  }

  const canShowItem = (rewardType: string) => {
    if (rewardType !== 'BonusCashReward' && bettingType === 'tote-racing') {
      return false
    }
    return true
  }

  return (
    <>
      <BonusBetListContainerStyled>
        <Swiper usePagination={true}>
          {paginatedBonusBets.map(chunk => (
            <BonusBetListItemContainerStyled key={paginatedBonusBets.indexOf(chunk)}>
              {chunk.map(
                item =>
                  canShowItem(item.rewardType) && (
                    <BonusBetListItem
                      key={item.id}
                      id={item.id}
                      name={item.title}
                      expiry={item.expiry}
                      amount={item.remainingAmount}
                      selected={getButtonState(item.id)}
                      disabled={isBusy || hasSelectedSuperPick || isEachWay}
                      handleClick={() => updateButtonState(item.id)}
                      {...(isCampaignV2(item) &&
                        isBetSplittingAndCampaignsV2Active && {
                          betCount: groupedBonusBets[item.id].length,
                          canBeSplit: item.tokenCanBeSplit || undefined,
                          openSplitBonusBetTray: () => {
                            setBonusBetToSplit(item)
                            trackBonusOfferClick(item, 'Split Bonus Bet')
                          },
                        })}
                    />
                  )
              )}
            </BonusBetListItemContainerStyled>
          ))}
        </Swiper>
      </BonusBetListContainerStyled>
      {isSplitBonusFeatureActive
        ? createPortal(
            <BonusBetSplittingTray
              bonusBetToSplit={bonusBetToSplit}
              isTrayOpen={isSplitBonusBetTrayOpen}
              closeTray={() => setBonusBetToSplit(null)}
              confirmSplitBonusBet={mutate}
              isBonusBetSplitPending={isPending}
            />,
            document.body
          )
        : null}
    </>
  )
}

function paginate(array: Campaign[] | undefined, size: number): Campaign[][] | null {
  if (!array) {
    return null
  }
  const chunks = []
  for (let i = 0; i < array.length; i += size) {
    const chunk = array.slice(i, i + size)
    chunks.push(chunk)
  }
  return chunks
}

export const BonusBetList = observeImmutable<
  BonusBetListStateProps & { canBet: boolean },
  BonusBetListExternalProps
>(state$, ({ record: state, ...externalProps }) => {
  return state.canBet ? <BonusBetListComponent {...externalProps} {...state} /> : null
})
